import { FC, useState } from "react";
import Layout from "components/Layout";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import CrudLayout from "components/CrudLayout";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
} from "@chakra-ui/react";
import SearchInput from "components/SearchInput";
import { FaFile } from "react-icons/fa";
import useAdmin from "hooks/useAdmin";
import { IAdmin, IAdminFormPayload } from "interfaces/admin";
import ActionsButton from "components/ActionsButton";
import { useDeleteDialog } from "hooks";
import { Form, Formik } from "formik";
import { adminValidation } from "utils/validationSchema";
import Select from "components/Select";
import TextInput from "components/TextInput";
import ConfirmDialog from "components/ConfirmDialog";
import { useAuthContext } from "components/AuthProvider";
import NotFound from "./NotFound";

const Admins: FC = () => {
  const [filter, setFilter] = useState("");
  const {
    data,
    loading,
    isLoadingAction,
    deleteAdmin,
    isOpenForm,
    onCloseForm,
    onSubmitForm,
    formValue,
    openAddForm,
    openEditForm,
  } = useAdmin();
  const path = "/admins";
  const { isOpenDelete, deleteId, onOpendelete, onCloseDelete } =
    useDeleteDialog();
  const columnHelper = createColumnHelper<IAdmin>();
  const { user } = useAuthContext();

  const columns = [
    columnHelper.accessor("name", {
      cell: (info) => info.getValue(),
      header: "Nama",
    }),
    columnHelper.accessor("email", {
      cell: (info) => info.getValue(),
      header: "Email",
    }),
    columnHelper.accessor("role", {
      cell: (info) => info.getValue().toUpperCase(),
      header: "Role",
    }),
    columnHelper.accessor("id", {
      header: () => null,
      cell: (info) => (
        <ActionsButton
          id={info.getValue()}
          actions={
            info.row.original.role === "admin"
              ? [
                  {
                    label: "Edit",
                    onClick: () => openEditForm(info.getValue()),
                  },
                  {
                    label: "Hapus",
                    onClick: () => onOpendelete(info.getValue()),
                  },
                ]
              : [
                  {
                    label: "Edit",
                    onClick: () => openEditForm(info.getValue()),
                  },
                ]
          }
        />
      ),
      meta: {
        sx: {
          textAlign: "right",
        },
      },
      enableGlobalFilter: false,
      enableSorting: false,
    }),
  ];

  if (user?.role !== "super") {
    return <NotFound />;
  }

  return (
    <Layout isPrivate>
      <CrudLayout breadcrumb={[{ title: "Admins", link: path }]}>
        <Stack direction="row" alignItems="center" my={6}>
          <SearchInput
            onSearch={(search) => {
              setFilter(search);
            }}
          />
          <Button onClick={openAddForm} leftIcon={<FaFile />}>
            Tambah
          </Button>
        </Stack>
        {data && (
          <DataTable
            isLoading={loading}
            columns={columns}
            data={data}
            filter={filter}
          />
        )}
      </CrudLayout>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpenForm}
        onClose={onCloseForm}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {formValue.id === "new" ? "Tambah" : "Ubah"} Data
          </ModalHeader>
          <ModalCloseButton />
          <Formik<IAdminFormPayload>
            onSubmit={onSubmitForm}
            initialValues={formValue.initialValues}
            enableReinitialize
            validationSchema={adminValidation}
          >
            <Form>
              <ModalBody>
                <Stack spacing={2}>
                  <TextInput name="name" id="name" label="Nama" />
                  <TextInput
                    isDisabled={formValue.id !== "new"}
                    name="email"
                    id="email"
                    label="Email"
                  />
                  <Select
                    label="Tipe Admin"
                    name="role"
                    id="houseType"
                    data={[
                      { label: "Super Admin", value: "super" },
                      { label: "Admin", value: "admin" },
                    ]}
                    placeholder="---"
                  />
                </Stack>
              </ModalBody>
              <ModalFooter>
                <Button
                  type="reset"
                  colorScheme="red"
                  mr={3}
                  onClick={onCloseForm}
                >
                  Cancel
                </Button>
                <Button
                  isLoading={isLoadingAction}
                  type="submit"
                  colorScheme="blue"
                >
                  Submit
                </Button>
              </ModalFooter>
            </Form>
          </Formik>
        </ModalContent>
      </Modal>
      <ConfirmDialog
        loading={isLoadingAction}
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        onConfirm={async () => {
          await deleteAdmin(deleteId);
        }}
      />
    </Layout>
  );
};

export default Admins;
